import { MoneyDirection } from "@common/constants";
import { CashFlowSectionId } from "@common/constants/reports/cashflow.constant";
import {
    CashFlowTableData,
    CashFlowTableRow,
} from "@common/dto/reports/cash-flow-tab-report-response.dto";
import { ReportFilters } from "@common/types/filters/reports";
import { TransactionStatus } from "@common/types/transaction";
import { TransactionView } from "@common/types/views/transactionViews";
import { TransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/TransactionViewDrawer";
import { DefaultCashflowDrillDownTransactionView } from "@components/transactions/views/DefaultCashflowDrillDownTransactionView";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { useConstant } from "@hooks/useConstant";
import { parse } from "date-fns";
import { uniq } from "@common/helpers/collections";
import React, { useCallback, useContext, useMemo } from "react";
import { useEffectiveBookkeepingStartDate } from "@hooks/useEffectiveBookkeepingStartDate";
import {
    getCycleEndForDate,
    getCycleStartForDate,
    getLabelFormatForCycle,
} from "@common/helpers/reports";
import { TransactionsFilters } from "../transactions/filters/lib";
import { NoMatchingTransactions } from "../transactions/NoMatchingTransactions";
import { NoTransactions } from "../transactions/NoTransactions";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../transactions/PaginatedTransactionsTable.context";
import { useFiltersActive } from "../transactions/useFiltersActive";
import { useCashFlowDrillDownContext } from "./CashFlowDrillDown.context";
import "./CashFlowDrillDownDrawer.scss";
import { CashFlowDrillDownHeader } from "./CashFlowDrillDownHeader";

interface CashFlowDrillDownModalProps {
    tableData: CashFlowTableData;
    filters: ReportFilters;
}

function findRowByPath(
    rows: CashFlowTableRow[],
    pathIds: string[],
): CashFlowTableRow | undefined {
    if (pathIds.length === 0) {
        return undefined;
    }

    const [currentId, ...restPathIds] = pathIds;

    for (const row of rows) {
        if (row.id === currentId) {
            if (restPathIds.length === 0) {
                return row;
            } else if (row.children) {
                return findRowByPath(row.children, restPathIds);
            } else {
                return undefined;
            }
        }
    }

    return undefined;
}

function getCategoriesFromCashFlowRowsByPath(
    rows: CashFlowTableRow[],
    pathIds: string[],
): {
    categoriesInRow: string[];
} {
    if (pathIds[0] === "cash-generated") {
        const cashIn = getCategoriesFromCashFlowRowsByPath(rows, ["cash-in"]);
        const cashOut = getCategoriesFromCashFlowRowsByPath(rows, ["cash-out"]);

        return {
            categoriesInRow: [
                ...cashIn.categoriesInRow,
                ...cashOut.categoriesInRow,
            ],
        };
    }

    let row = findRowByPath(rows, pathIds);

    if (row?.isTotal) {
        row = findRowByPath(rows, pathIds.slice(0, -1));
    }

    const categoriesInRow = row ? getCategoriesFromRows([row]) : [];

    return {
        categoriesInRow,
    };
}

function getCategoriesFromRows(rows: CashFlowTableRow[]): string[] {
    return rows.flatMap((row) => {
        const categories = row.filters?.category ?? [];
        const childCategories = row.children
            ? getCategoriesFromRows(row.children)
            : [];
        return [...categories, ...childCategories];
    });
}

export const CashFlowDrillDownDrawer: React.FC<CashFlowDrillDownModalProps> = ({
    tableData,
    filters,
}) => {
    const { params, close } = useCashFlowDrillDownContext();
    const shouldShowModal = params != null;
    const {
        category,
        path,
        columnId,
        direction: directionFromParams,
    } = params ?? {};
    const parseReferenceDate = useConstant(() => new Date());

    const parentFilters = useMemo<TransactionsFilters>(() => {
        const { cycle, ...filtersWithoutCycle } = filters;
        const pathIds = path?.map((p) => p.id) ?? [];
        const { categoriesInRow } = getCategoriesFromCashFlowRowsByPath(
            tableData.rows,
            pathIds,
        );

        const categoryToUse = uniq(category ?? categoriesInRow);

        let newDates = {};
        if (columnId && !["percent", "title", "total"].includes(columnId)) {
            const columnDate = parse(
                columnId,
                getLabelFormatForCycle(cycle),
                parseReferenceDate,
            );
            const startDate = getCycleStartForDate(columnDate, cycle);
            const endDate = getCycleEndForDate(columnDate, cycle);

            newDates = {
                start: startDate,
                end: endDate,
            };
        }

        let direction: MoneyDirection | undefined = directionFromParams;

        if (pathIds.includes(CashFlowSectionId.cashIn)) {
            direction = MoneyDirection.MONEY_IN;
        } else if (pathIds.includes(CashFlowSectionId.cashOut)) {
            direction = MoneyDirection.MONEY_OUT;
        }

        return {
            ...filtersWithoutCycle,
            ...newDates,
            category: categoryToUse,
            statuses: [TransactionStatus.PENDING, TransactionStatus.COMPLETED],
            counterpartiesIds: params?.counterpartyId,
            withEmptyOrUnverifiedCounterparty:
                params?.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: params?.hasCounterparty,
            direction,
        };
    }, [
        category,
        filters,
        columnId,
        tableData,
        path,
        params?.counterpartyId,
        directionFromParams,
        params?.withEmptyOrUnverifiedCounterparty,
        params?.hasCounterparty,
        parseReferenceDate,
    ]);

    const renderEmptyState = useCallback(
        () => (
            <CashFlowDrillDownModalEmptyState parentFilters={parentFilters} />
        ),
        [parentFilters],
    );

    const view: TransactionView = useMemo(
        () => ({
            ...DefaultCashflowDrillDownTransactionView,
            options: {
                bulkActions: false,
                selection: true,
            },
            filters: parentFilters,
        }),
        [parentFilters],
    );

    return (
        <TransactionViewDrawer
            view={view}
            open={shouldShowModal}
            cacheKey={WORKSPACE_USER_CACHE_KEYS.CASHFLOW_DRILLDOWN_CACHE_KEY}
            onClose={close}
            parentFilters={parentFilters}
            headerAddon={() => (
                <CashFlowDrillDownHeader defaultView={view} path={path} />
            )}
            emptyView={renderEmptyState}
        />
    );
};

interface CashFlowDrillDownModalEmptyStateProps {
    parentFilters: TransactionsFilters;
}

const CashFlowDrillDownModalEmptyState: React.FC<
    CashFlowDrillDownModalEmptyStateProps
> = ({ parentFilters }) => {
    const { filters: currentFilters } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters } = useContext(
        PaginatedTransactionsActionsContext,
    );
    const filtersActive = useFiltersActive(currentFilters);
    const bookkeepingStartDate = useEffectiveBookkeepingStartDate(
        currentFilters.entityIds,
    );

    if (filtersActive) {
        return (
            <NoMatchingTransactions
                onResetFilters={() => setCurrentFilters(parentFilters)}
                bookkeepingStartDate={bookkeepingStartDate}
            />
        );
    }

    return <NoTransactions />;
};
